import cn from 'classnames';
import React, { useMemo } from 'react';
import {
  SECTOR_INDUSTRY_TOOLTIP,
  getApprovedColumnTooltip,
} from './constants';
import { DataTable, SortOrder } from '@dealroadshow/uikit-table/DataTable';
import Spinner from '@dealroadshow/uikit-loader/Spinner';
import Tooltip from '@dealroadshow/uikit-tooltip';
import alignStyles from '@dealroadshow/uikit-styles/helpers/align.module.scss';
import spacesStyles from '@dealroadshow/uikit-styles/helpers/spaces.module.scss';
import stylesForSet from '../../Set/set.module.scss';
import styles from '../datarooms.module.scss';
import sharedConfig from "@dataroom/config";
import {
  cellCountFilesCallback,
  cellEmailsCallback,
  cellLastActivityCallback,
  cellLastActivityExploreSetsCallback,
  cellLastActivityExploreSetsWithSessionCheckCallback,
  cellNameCallback,
  cellNameNotClickableCallback,
  cellStageCallback,
  cellPermissionsCallback,
  cellSectorCallback,
  cellSizeCallback,
  cellSponsorNameCallback,
  cellStatusIndicatorCallback,
  cellUsersCallback,
  cellApprovedCallback,
  cellUnderwriterNameCallback,
  cellStepsToCompleteCallback,
  cellBankNameCallback,
  cellLastActivityExploreSetsWithCountryCheckCallback,
  cellDeclarationCallback,
  cellTransactionTypeCallback,
} from './cellCallbacks';

import Column from '../../common/Column';
import { DataroomTenant } from '@dataroom/tenant/DataroomTenant';
import { useDataroomAvailabilityContext } from '../../../contexts/DataroomAvailabilityContext';
import { ICellProps, IColumn } from '@dealroadshow/uikit-table/DataTable';

interface IProps {
  collection: Array<any>,
  isFetching: boolean,
  sortBy: string,
  sortOrder: SortOrder,
  className: string,
  onSortChange: (sortBy: string, sortOrder: string) => void,
  tenant: DataroomTenant,
  columns: Array<Column>,
  isExploreSetsTab: boolean,
}

const DataroomsTable = ({
  isExploreSetsTab = false,
  tenant,
  collection,
  isFetching,
  sortBy,
  sortOrder,
  className,
  onSortChange,
  columns,
}: IProps) => {
  const { isDisabled } = useDataroomAvailabilityContext();
  const isResearchRoom = tenant === sharedConfig.researchroom.code;
  const isSet = tenant === sharedConfig.investorset.code || tenant === sharedConfig.verisend.code
    || isResearchRoom;
  const tableStyles = isSet ? stylesForSet : styles; // TODO try remove isSet from this file
  const isMySetsTab = isSet && !isExploreSetsTab;

  const getColumns = (columns: string[]) => columns.reduce<IColumn[]>((specificColumns, key) => {
    if (key in allColumns) {
      specificColumns.push((allColumns as Record<string, IColumn>)[key]);
    }
    return specificColumns;
  }, []);

  const allColumns = useMemo(() => ({
    [Column.Sector]: {
      name: 'SEC/IND',
      width: 44,
      title: (
        <Tooltip
          content={ SECTOR_INDUSTRY_TOOLTIP }
          usePortal={ false }
        >
          <span>SEC/IND</span>
        </Tooltip>
      ),
      className: cn(tableStyles.colSector, alignStyles.alignCenter),
      cellCallback: cellSectorCallback,
    },
    [Column.Status]: {
      name: 'status',
      width: 30,
      title: 'Status',
      className: cn(tableStyles.colStatus, alignStyles.alignCenter),
      cellCallback: cellStatusIndicatorCallback,
      sortable: true,
    },
    [Column.Name]: {
      name: 'name',
      title: 'Name',
      className: cn(
        tableStyles.colName,
        { [tableStyles.columnNameMySets]: isMySetsTab },
      ),
      cellCallback: (props: ICellProps) => cellNameCallback(props, isDisabled(props)),
      sortable: true,
    },
    [Column.Bank]: {
      name: 'Bank',
      title: 'Bank',
      cellCallback: (props: ICellProps) => cellBankNameCallback(props, isDisabled(props)),
    },
    [Column.TransactionType]: {
      name: 'transactionType',
      title: 'Type',
      className: tableStyles.colTransactionType,
      cellCallback: (props: ICellProps) => cellTransactionTypeCallback(props, isDisabled(props)),
      sortable: true,
    },
    [Column.ArrangerAgent]: {
      name: 'underwriters',
      title: 'Arranger / Agent',
      className: tableStyles.colArrangerAgent,
      cellCallback: (props: ICellProps) => cellUnderwriterNameCallback(props, isDisabled(props), tableStyles.arrangerAgent),
    },
    [Column.Stage]: {
      name: 'stage',
      title: '',
      width: 30,
      className: cn(tableStyles.colStatus, alignStyles.alignCenter),
      cellCallback: cellStageCallback,
      sortable: false,
    },
    [Column.NameNotClickable]: {
      name: 'name',
      title: 'Name',
      className: cn(tableStyles.colName, tableStyles.specificWidth),
      cellCallback: (cellProps: ICellProps) => (cellProps.row?.accessRequest?.isFollowing
        ? cellNameCallback(cellProps, isDisabled(cellProps))
        : cellNameNotClickableCallback(cellProps)),
      sortable: true,
    },
    [Column.Sponsor]: {
      name: 'sponsor',
      title: 'parent',
      className: cn(
        tableStyles.colSponsor,
        { [tableStyles.colSponsorMySets]: isMySetsTab },
        { [spacesStyles.plm]: isSet },
      ),
      cellCallback: (props: ICellProps) => cellSponsorNameCallback(props, isDisabled(props)),
      sortable: true,
    },
    [Column.Underwriter]: {
      name: 'Underwriter',
      title: 'Underwriter',
      cellCallback: (props: ICellProps) => cellUnderwriterNameCallback(props, isDisabled(props)),
    },
    [Column.Approved]: {
      name: 'approved',
      width: 80,
      title: (
        <Tooltip
          content={ getApprovedColumnTooltip(tenant) }
          usePortal={ false }
        >
          <span>approved</span>
        </Tooltip>
      ),
      className: cn(
        tableStyles.colApproved,
        alignStyles.alignCenter,
        spacesStyles.plm,
      ),
      cellCallback: (props: ICellProps) => cellApprovedCallback(props, tenant, isDisabled(props)),
      sortable: false,
    },
    [Column.Permissions]: {
      name: 'permissions',
      title: isSet ? 'Permissions' : 'My Permissions',
      className: cn(tableStyles.colPermissions, {
        [spacesStyles.plm]: isSet,
      }),
      cellCallback: (props: ICellProps) => cellPermissionsCallback(props, isDisabled(props)),
      sortable: true,
    },
    [Column.Users]: {
      name: 'users',
      title: 'Users',
      className: cn(
        tableStyles.colUsers,
        alignStyles.alignRight,
        { [spacesStyles.plm]: isSet },
      ),
      cellCallback: (props: ICellProps) => cellUsersCallback(props, isDisabled(props)),
      sortable: true,
    },
    [Column.Files]: {
      name: 'files',
      title: 'Files',
      className: cn(
        alignStyles.alignRight,
        tableStyles.colFiles,
        { [spacesStyles.plm]: isSet },
      ),
      cellCallback: (props: ICellProps) => cellCountFilesCallback(props, isDisabled(props)),
      sortable: true,
    },
    [Column.Size]: {
      name: 'size',
      title: 'Size',
      className: cn(
        tableStyles.colSize,
        alignStyles.alignRight,
        { [spacesStyles.plm]: isSet },
      ),
      cellCallback: (props: ICellProps) => cellSizeCallback(props, isDisabled(props)),
      sortable: true,
    },
    [Column.EmailAlerts]: {
      name: 'emailAlerts',
      width: 210,
      title: 'email Alerts',
      className: cn(
        tableStyles.colAlerts,
        { [spacesStyles.plm]: isSet },
      ),
      cellCallback: (cellProps: ICellProps) => cellEmailsCallback(cellProps, isDisabled(cellProps)),
    },
    [Column.LastActivity]: {
      name: 'lastActivity',
      width: isResearchRoom ? 325 : 200,
      title: 'last activity',
      className: cn(
        tableStyles.colLastDate,
        alignStyles.alignRight,
        spacesStyles.ptn,
        spacesStyles.pbn,
        { [spacesStyles.plm]: isSet },
      ),
      cellCallback: (props: ICellProps) => cellLastActivityCallback(props, isDisabled(props)),
      sortable: true,
    },
    [Column.LastActivityExploreSets]: {
      name: 'lastActivity',
      width: 240,
      title: 'last activity',
      className: cn(
        tableStyles.colLastDate,
        alignStyles.alignRight,
        spacesStyles.ptn,
        spacesStyles.pbn,
        { [spacesStyles.plm]: isSet },
      ),
      cellCallback: (props: ICellProps) => cellLastActivityExploreSetsCallback(props, isDisabled(props)),
      sortable: true,
    },
    [Column.LastActivityExploreSetsWithSessionCheck]: {
      name: 'lastActivity',
      width: 170,
      title: 'last activity',
      className: cn(
        tableStyles.colLastDate,
        alignStyles.alignRight,
        spacesStyles.ptn,
        spacesStyles.pbn,
        { [spacesStyles.plm]: isSet },
      ),
      cellCallback: (props: ICellProps) => cellLastActivityExploreSetsWithSessionCheckCallback(props, isDisabled(props)),
      sortable: true,
    },
    [Column.LastActivityExploreSetsWithCountryCheckCallback]: {
      name: 'lastActivity',
      width: 170,
      title: 'last activity',
      className: cn(
        tableStyles.colLastDate,
        alignStyles.alignRight,
        spacesStyles.ptn,
        spacesStyles.pbn,
        { [spacesStyles.plm]: isSet },
      ),
      cellCallback: (props: ICellProps) => cellLastActivityExploreSetsWithCountryCheckCallback(props, isDisabled(props)),
      sortable: true,
    },
    [Column.StepsToComplete]: {
      name: 'stage',
      width: 170,
      title: 'steps to complete',
      className: cn(
        tableStyles.colAlerts,
        { [spacesStyles.plm]: isSet },
      ),
      cellCallback: cellStepsToCompleteCallback,
    },
    [Column.Declaration]: {
      name: 'userDeclaration',
      width: 110,
      title: 'Declaration',
      className: cn(alignStyles.alignCenter, tableStyles.colDeclaration),
      cellCallback: (props: ICellProps) => cellDeclarationCallback(props, isDisabled(props)),
      sortable: true,
    },
  }), []);

  return (
    <DataTable
      noWrapCells
      containerClassName={ tableStyles.dataTableContainer }
      className={ className }
      dataTest="dataRoomsDataTable"
      columns={ getColumns(columns) }
      data={ collection }
      isFetching={ isFetching }
      loadingComponent={ Spinner }
      onSortChange={ onSortChange }
      sortBy={ sortBy }
      sortOrder={ sortOrder }
    />
  );
};

export default DataroomsTable;
